// Web-compatible component wrappers for React Native components
import React from 'react';

// Utility function to flatten and merge style arrays (React Native style)
const flattenStyle = (style) => {
  if (!style) return {};
  if (Array.isArray(style)) {
    return style.reduce((acc, s) => {
      if (!s) return acc;
      const flattened = flattenStyle(s);
      return { ...acc, ...flattened };
    }, {});
  }
  if (typeof style === 'object') {
    return style;
  }
  return {};
};

// Convert React Native style to web CSS
const convertStyle = (style) => {
  const webStyle = { ...style };
  
  // Convert flex: 1 to flex: '1 1 0%' for web
  if (webStyle.flex === 1) {
    webStyle.flex = '1 1 0%';
  }
  
  // Ensure display flex when using flex properties
  if (webStyle.flex || webStyle.flexDirection || webStyle.justifyContent || webStyle.alignItems) {
    webStyle.display = webStyle.display || 'flex';
  }
  
  // Remove React Native specific properties that don't work on web
  delete webStyle.elevation;
  delete webStyle.shadowColor;
  delete webStyle.shadowOffset;
  delete webStyle.shadowOpacity;
  delete webStyle.shadowRadius;
  
  return webStyle;
};

// View -> div
export const View = ({ style, children, ...props }) => {
  const flattened = flattenStyle(style);
  const webStyle = convertStyle(flattened);
  
  return (
    <div style={webStyle} {...props}>
      {children}
    </div>
  );
};

// Text -> span/p/div
export const Text = ({ style, children, ...props }) => {
  const Tag = props.h1 ? 'h1' : props.h2 ? 'h2' : props.h3 ? 'h3' : props.h4 ? 'h4' : props.p ? 'p' : 'span';
  const flattened = flattenStyle(style);
  const webStyle = convertStyle(flattened);
  
  return (
    <Tag style={webStyle} {...props}>
      {children}
    </Tag>
  );
};

// TouchableOpacity -> button or div with onClick
export const TouchableOpacity = ({ style, onPress, children, disabled, ...props }) => {
  const handleClick = (e) => {
    if (!disabled && onPress) {
      onPress(e);
    }
  };

  const flattened = flattenStyle(style);
  const webStyle = convertStyle(flattened);
  const buttonStyle = {
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    border: 'none',
    background: 'transparent',
    ...webStyle,
  };

  return (
    <button
      type="button"
      style={buttonStyle}
      onClick={handleClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

// ScrollView -> div with overflow
export const ScrollView = ({ style, children, contentContainerStyle, refreshControl, ...props }) => {
  const flattened = flattenStyle(style);
  const contentFlattened = flattenStyle(contentContainerStyle);
  const scrollStyle = convertStyle({
    overflowY: 'auto',
    overflowX: 'hidden',
    ...flattened,
  });
  const contentStyle = convertStyle(contentFlattened);

  return (
    <div style={scrollStyle} {...props}>
      <div style={contentStyle}>
        {children}
      </div>
    </div>
  );
};

// SafeAreaView -> div
export const SafeAreaView = ({ style, children, ...props }) => {
  const flattened = flattenStyle(style);
  const webStyle = convertStyle(flattened);
  
  return (
    <div style={webStyle} {...props}>
      {children}
    </div>
  );
};

// TextInput -> input
export const TextInput = ({ style, value, onChangeText, placeholder, secureTextEntry, multiline, numberOfLines, placeholderTextColor, ...props }) => {
  const flattened = flattenStyle(style);
  const webStyle = convertStyle(flattened);
  
  // Filter out React Native specific props that shouldn't be passed to DOM
  const { placeholderTextColor: _, ...domProps } = props;
  
  const inputStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #ccc',
    borderRadius: '8px',
    ...webStyle,
  };

  // Create a style tag for placeholder color if needed
  const placeholderStyleId = `placeholder-${placeholderTextColor?.replace(/[^a-zA-Z0-9]/g, '') || 'default'}`;
  
  if (multiline) {
    return (
      <>
        {placeholderTextColor && (
          <style>
            {`
              #${placeholderStyleId}::placeholder {
                color: ${placeholderTextColor} !important;
              }
              #${placeholderStyleId}::-webkit-input-placeholder {
                color: ${placeholderTextColor} !important;
              }
              #${placeholderStyleId}::-moz-placeholder {
                color: ${placeholderTextColor} !important;
                opacity: 1;
              }
              #${placeholderStyleId}:-ms-input-placeholder {
                color: ${placeholderTextColor} !important;
              }
            `}
          </style>
        )}
        <textarea
          id={placeholderTextColor ? placeholderStyleId : undefined}
          style={{ ...inputStyle, minHeight: numberOfLines ? `${numberOfLines * 24}px` : '80px' }}
          value={value}
          onChange={(e) => onChangeText && onChangeText(e.target.value)}
          placeholder={placeholder}
          {...domProps}
        />
      </>
    );
  }

  return (
    <>
      {placeholderTextColor && (
        <style>
          {`
            #${placeholderStyleId}::placeholder {
              color: ${placeholderTextColor} !important;
            }
            #${placeholderStyleId}::-webkit-input-placeholder {
              color: ${placeholderTextColor} !important;
            }
            #${placeholderStyleId}::-moz-placeholder {
              color: ${placeholderTextColor} !important;
              opacity: 1;
            }
            #${placeholderStyleId}:-ms-input-placeholder {
              color: ${placeholderTextColor} !important;
            }
          `}
        </style>
      )}
      <input
        id={placeholderTextColor ? placeholderStyleId : undefined}
        type={secureTextEntry ? 'password' : 'text'}
        style={inputStyle}
        value={value}
        onChange={(e) => onChangeText && onChangeText(e.target.value)}
        placeholder={placeholder}
        {...domProps}
      />
    </>
  );
};

// ActivityIndicator -> loading spinner
export const ActivityIndicator = ({ size = 'large', color = '#60A5FA', style }) => {
  const flattened = flattenStyle(style);
  const webStyle = convertStyle(flattened);
  const spinnerStyle = {
    border: `3px solid ${color}20`,
    borderTop: `3px solid ${color}`,
    borderRadius: '50%',
    width: size === 'large' ? '40px' : '20px',
    height: size === 'large' ? '40px' : '20px',
    animation: 'spin 1s linear infinite',
    ...webStyle,
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={spinnerStyle} />
    </>
  );
};

// Alert -> window.alert or custom modal (simplified for now)
export const Alert = {
  alert: (title, message, buttons) => {
    if (buttons && buttons.length > 0) {
      // For web, we'll use confirm for simple cases
      const result = window.confirm(`${title}\n\n${message}`);
      if (result && buttons[0]?.onPress) {
        buttons[0].onPress();
      }
    } else {
      window.alert(`${title}\n\n${message}`);
    }
  },
  confirm: window.confirm,
};

// Platform
export const Platform = {
  OS: 'web',
  select: (obj) => obj.web || obj.default,
};

// Linking -> window.location and URL handling
export const Linking = {
  openURL: (url) => {
    window.open(url, '_blank');
  },
  canOpenURL: async () => true,
  getInitialURL: async () => {
    return window.location.href;
  },
  addEventListener: (event, callback) => {
    if (event === 'url') {
      // Handle URL changes (for deep linking)
      const handlePopState = () => {
        callback({ url: window.location.href });
      };
      window.addEventListener('popstate', handlePopState);
      return {
        remove: () => window.removeEventListener('popstate', handlePopState),
      };
    }
    return { remove: () => {} };
  },
};

// KeyboardAvoidingView -> div (no special behavior needed on web)
export const KeyboardAvoidingView = ({ style, children, behavior, ...props }) => {
  const flattened = flattenStyle(style);
  const webStyle = convertStyle(flattened);
  
  return (
    <div style={webStyle} {...props}>
      {children}
    </div>
  );
};

// RefreshControl -> handled in ScrollView
export const RefreshControl = ({ refreshing, onRefresh }) => {
  // This is handled by the parent ScrollView
  return null;
};

// MaterialIcons - use @mui/icons-material directly in components
// This is just a placeholder export for compatibility
export const MaterialIcon = ({ name, size = 24, color = 'currentColor', style }) => {
  console.warn('MaterialIcon component is deprecated. Use @mui/icons-material directly.');
  return null;
};

// StyleSheet helper (returns styles as-is for web)
export const StyleSheet = {
  create: (styles) => styles,
  flatten: flattenStyle,
};

